import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BriefcaseBusiness, Eye, Plus } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { ActionButton } from '../../shared/ui/ActionBar';
import { EmptyState } from '../../shared/ui/EmptyState';

const formatSalary = (value, currency) =>
  value || value === 0 ? `${value} ${currency || ''}`.trim() : '—';

const PAGE_SIZE = 5;

export const VacanciesPage = () => {
  const [data, setData] = useState({});
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const categoryId = searchParams.get('categoryId') || null;
  const [searchValue, setSearchValue] = useState(q);
  const [filterValues, setFilterValues] = useState({ categoryId });
  const [reloadKey, setReloadKey] = useState(0);
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);

  // Старые ссылки вида ?search=… — переносим в общий для всех списков ?q=
  useEffect(() => {
    const legacy = searchParams.get('search');
    if (legacy === null) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('search');
        if (legacy && !params.get('q')) params.set('q', legacy);
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Черновики поиска и фильтра следуют за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchValue(q);
  }, [q]);

  useEffect(() => {
    setFilterValues({ categoryId });
  }, [categoryId]);

  useEffect(() => {
    apiRequests.hr.getCategories(100).then((res) => setCategories(res.items));
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiRequests.hr
      .get(pageSize, offset, categoryId, q)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((error) => {
        console.error('Ошибка при получении вакансий:', error);
        if (!cancelled) setData({ items: [], totalCount: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [q, categoryId, offset, pageSize, reloadKey]);

  const updateParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  const handleSearch = () => updateParams({ q: searchValue.trim() });

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => updateParams(filterValues);

  const resetFilters = () => {
    setSearchValue('');
    setFilterValues({ categoryId: null });
    updateParams({ q: null, categoryId: null });
  };

  const handleDelete = (id) => {
    apiRequests.hr.deleteVacancy(id).then(() => {
      if (data.items?.length === 1 && page > 1) setPage(page - 1);
      else setReloadKey((k) => k + 1);
    });
  };

  const filters = [
    {
      key: 'categoryId',
      type: 'select',
      label: 'Категория',
      value: filterValues.categoryId,
      placeholder: 'Все категории',
      options: categories?.map((c) => ({ value: c.id, label: c.title })) || [],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск вакансий..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        createLink="/HR/vacancies/create"
      />

      <div className="mb-3">
        {data.items?.length === 0 ? (
          q || categoryId ? (
            <EmptyState
              icon={BriefcaseBusiness}
              variant="search"
              title="Вакансии не найдены"
              description="Ни одна вакансия не подходит под запрос и выбранную категорию. Измените формулировку или сбросьте фильтры."
            />
          ) : (
            <EmptyState
              icon={BriefcaseBusiness}
              title="Открытых вакансий пока нет"
              description="Опубликуйте первую вакансию — отклики кандидатов появятся здесь же, в карточке вакансии."
              action={{ label: 'Добавить вакансию', to: '/HR/vacancies/create', icon: Plus }}
            />
          )
        ) : (
          data.items?.map((item) => (
            <EntityCard
              key={item.id}
              title={item.title}
              fields={[
                { label: 'Ставка от', value: formatSalary(item.salaryFrom, item.currency) },
                { label: 'Ставка до', value: formatSalary(item.salaryTo, item.currency) },
                { label: 'Создано', value: dateToString(item.createdAt) },
              ]}
              editLink={`/HR/vacancies/${item.id}`}
              onDelete={() => handleDelete(item.id)}
              actions={
                <ActionButton
                  icon={Eye}
                  to={`/HR/vacancies/${item.id}/summaries`}
                  title="Просмотр заявок"
                  variant="primary"
                />
              }
            />
          ))
        )}
      </div>

      <Pagination page={page} total={data.totalCount} pageSize={pageSize} onPageChange={setPage} />
    </>
  );
};
